package us.laantech.laanmanage.feature.scope

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.ParentCompany
import us.laantech.common.ui.DropdownMenu
import us.laantech.common.api.KtorClient


data class ParentCompanyScopeUiState(
    val parentCompanies: List<ParentCompany> = emptyList(),
    val selectedParentCompany: ParentCompany? = null,
    val companyBranches: List<CompanyBranch> = emptyList(),
    val selectedCompanyBranch: CompanyBranch? = null
)

@Composable
fun ParentCompanyScope(
    requireBranch: Boolean = true,
    modifier: Modifier = Modifier,
    viewModel: ParentCompanyScopeViewModel = viewModel(
        factory = viewModelFactory {
            initializer { ParentCompanyScopeViewModel() }
        }
    ),
    content: @Composable (parentCompany: ParentCompany, companyBranch: CompanyBranch?) -> Unit
) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    Column(modifier.fillMaxSize()) {
        Row(Modifier.fillMaxWidth()) {
            if (uiState.parentCompanies.size > 1) {
                DropdownMenu(
                    label = "Parent Company",
                    items = uiState.parentCompanies,
                    selectedItem = uiState.selectedParentCompany,
                    itemLabel = { it.companyInfo?.companyName ?: "Company Name not set" },
                    onItemSelected = { viewModel.selectParentCompany(it) },
                    modifier = Modifier.weight(1f)
                )
            }

            if (requireBranch && uiState.companyBranches.size > 1) {
                DropdownMenu(
                    label = "Branch",
                    items = uiState.companyBranches,
                    selectedItem = uiState.selectedCompanyBranch,
                    itemLabel = { it.branchName ?: "Company Name not set" },
                    onItemSelected = { viewModel.selectCompanyBranch(it) },
                    modifier = Modifier.weight(1f)
                )
            }
        }

        if (uiState.selectedParentCompany != null) {
            if (!requireBranch || uiState.selectedCompanyBranch != null) {
                content(uiState.selectedParentCompany!!, uiState.selectedCompanyBranch)
            }
        }
    }

}

class ParentCompanyScopeViewModel(): ViewModel() {

    private val _uiState = MutableStateFlow(ParentCompanyScopeUiState())
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client

    init {
        viewModelScope.launch { loadParentCompanies() }
    }

    suspend fun loadParentCompanies() {
        // Fetch parent companies from API
        val response = ktClient.get("/backoffice/companies/list")
        if (response.status.isSuccess()) {
            val parentCompanies = response.body<List<ParentCompany>>()

            _uiState.update { it.copy(
                parentCompanies = parentCompanies,
                selectedParentCompany = if (parentCompanies.size == 1) parentCompanies.first() else null
            ) }

            loadCompanyBranches()
        }
    }

    fun selectParentCompany(parentCompany: ParentCompany?) {
        viewModelScope.launch {
            _uiState.update { it.copy(
                selectedParentCompany = parentCompany,
                companyBranches = emptyList(),
                selectedCompanyBranch = null) }
            loadCompanyBranches()
        }
    }

    suspend fun loadCompanyBranches() {
        if (uiState.value.selectedParentCompany == null) return

        val response = ktClient.get("/backoffice/store-management/list") {
            parameter(CompanyBranch::parentCompanyUuid.name, uiState.value.selectedParentCompany?.uuid)
        }
        if (response.status.isSuccess()) {
            val companyBranches = response.body<List<CompanyBranch>>()

            _uiState.update { it.copy(
                companyBranches = companyBranches,
                selectedCompanyBranch = if (companyBranches.size == 1) companyBranches.first() else null
            ) }

        }
    }

    fun selectCompanyBranch(companyBranch: CompanyBranch?) {
        _uiState.update { it.copy(selectedCompanyBranch = companyBranch) }
    }

}


